import React, { useState } from 'react';
import { APP_CONFIG } from 'utils/constants';
import CustomLink from '../CustomLink';
import Drawer from '../Drawer';
import './styles.scss';

const links = [
  {
    title: 'Agreement',
    to: 'agreement',
  },
  {
    title: 'Information Collection and Use; Choice and Consent',
    to: 'information_collection',
  },
  {
    title: 'Analytics',
    to: 'analytics',
  },
  {
    title: 'Withdrawing Consent',
    to: 'withdrawing_consent',
  },
  {
    title: 'Protecting Your Information',
    to: 'protection',
  },
  {
    title: 'Information Sharing and Disclosure',
    to: 'disclosure',
  },
  {
    title:
      'Links to Other Sites; Third Party Apps; Transactions with Third Parties',
    to: 'links',
  },
  {
    title: 'Children',
    to: 'children',
  },
  {
    title:
      'Your California Privacy Rights; California Do Not Track Disclosures',
    to: 'rights',
  },
  {
    title: 'Accessing Your Information',
    to: 'access',
  },
  {
    title:
      'Contact Us About Complaints, Questions or Notices Relating to this Privacy Policy',
    to: 'contact_us',
  },
  {
    title: 'Terms of Service',
    to: 'terms',
  },
];

const Privacy = () => {
  const [leftMenuIsOpen, setLeftMenuIsOpen] = useState<boolean>(false);

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setLeftMenuIsOpen(open);
  };

  return (
    <div data-testid="privacy-page" styleName="row">
      <div styleName="side-nav">
        <CustomLink links={links} />
      </div>
      <div styleName="legal-content">
        <Drawer
          open={leftMenuIsOpen}
          toggleDrawer={toggleDrawer}
          links={links}
        />
        <h2>
          PEAR THERAPEUTICS, INC.
          <br />
          PRIVACY POLICY FOR WWW.RESET.MD, WWW.RESET-O.MD AND WWW.PEAR.MD
        </h2>
        <p>
          <strong>This service is provided by Pear Therapeutics, Inc.</strong>
        </p>
        <p>
          Through its websites www.reset.md, www.reset-o.md, and www.pear.md
          (the <strong>&ldquo;Websites&rdquo;</strong>
          &nbsp;or <strong>&ldquo;Sites&rdquo;</strong>) and its mobile
          application for Patients (the <strong>&ldquo;App&rdquo;</strong>),
          Pear Therapeutics, Inc.{' '}
          <strong>
            (&ldquo;Pear Therapeutics&rdquo;, &ldquo;we&rdquo; or
            &ldquo;us&rdquo;){' '}
          </strong>
          provides prescription digital therapeutics designed to improve patient
          outcomes. These prescription therapies include data collection,
          storage, analysis and reporting tools, functions and related services,
          which are collectively referred to in this Privacy Policy as the{' '}
          <em>
            <strong>Service</strong>
          </em>
          .
        </p>
        <p>
          This Privacy Policy covers how Pear Therapeutics collects, receives,
          uses, retains, and discloses Personally Identifiable Information
          (&ldquo;PII&rdquo;) of Health Care Providers (also &ldquo;you&rdquo;,
          &ldquo;your&rdquo; or &ldquo;user&rdquo;) through the Sites. PII
          includes information about you that is personally identifying such as
          your name, email address, and phone number and which is not otherwise
          publicly available. Only the legal definition of PII that applies to
          your location will apply to you under this Privacy Policy.
        </p>
        <p>
          <em>
            <strong>Clinical Partners</strong>
          </em>{' '}
          are hospitals, clinics, practices or other medical groups or
          healthcare systems that have contracted with Pear Therapeutics to
          permit use of the Service by their respective Health Care Providers
          and Patients;{' '}
          <em>
            <strong>Health Care Providers</strong>
          </em>{' '}
          are physicians, counselors, practitioners, patient advocates, coaches,
          medical administrators, patient adherence managers, clinical
          administrators or other individuals who provide healthcare-related
          services to Patients (as well as certain employees of Patient Service
          Partners or contractors to a Clinical Partner);{' '}
          <em>
            <strong>Patient Service Partners</strong>
          </em>{' '}
          are service providers, including the provider of Pear’s patient
          services center, that have contracted with Pear to facilitate the use
          of the Pear Service by their respective Health Care Providers and
          Patients; and{' '}
          <em>
            <strong>Patients</strong>
          </em>{' '}
          are individual patients of the Clinical Partner who receive medical
          treatments or other healthcare services from one or more Health Care
          Providers, or individuals who are properly authorized representatives
          of any such patient.
        </p>
        <p>
          Through their provision of health care services to Patients, Health
          Care Providers and Clinical Partners will have access to and be
          responsible for Patient PII and Patient Protected Health Information
          as defined by Health Insurance Portability and Accountability Act
          (HIPAA) Privacy Rules or other applicable laws. Health Care Providers
          and Clinical Partners are responsible for the privacy and security of
          such information.
        </p>

        <h3 name="agreement">Agreement</h3>
        <p>
          <strong>
            By using the Sites or the Service, and/or by providing PII to Pear
            Therapeutics, you accept and hereby expressly consent to our
            collection, use, retention, and disclosure of your PII in accordance
            with the terms of this Privacy Policy. If you choose not to provide
            the requested information you will not be able to access the Sites
            or Service.
          </strong>
        </p>
        <p>
          This Privacy Policy may change from time to time, so please check back
          periodically to check the most recent modification date to ensure that
          you are aware of any changes in our processing of your PII. Your
          continued use of the Sites or the Service after any changes signifies
          your express, explicit, voluntary and unambiguous consent to any such
          changes. If you do not agree to such changes, you must immediately
          stop using the Sites and notify us by email at{' '}
          <a href={`mailto:${APP_CONFIG.optoutEmail}`}>
            {APP_CONFIG.optoutEmail}
          </a>
          . This Privacy Policy was last modified on June 1, 2019.
        </p>

        <h3 name="information_collection">
          Information Collection and Use; Choice and Consent
        </h3>

        <p>
          <em>Registration</em>
        </p>
        <p>
          Your name and email address are used to set up an account in order to
          use the Service. We may receive PII about you from your Clinical
          Partner in order to identify you as an authorized user of the Service.
          It is always your choice whether or not to provide us with such
          information. In order to utilize our service, however, you are
          required to provide this information.
        </p>
        <p>Pear Therapeutics uses PII to:</p>
        <ol type="a">
          <li>Provide the Service</li>
          <li>Communicate with you</li>
          <li>
            Communicate with your Patients and Clinical Partners as applicable
          </li>
          <li>Create user profiles</li>
          <li>Create de-identified analytical information</li>
          <li>Reply to your request for information or comments</li>
        </ol>

        <h3 name="analytics">Analytics</h3>
        <p>
          When you use the Sites or the Service, we use tracking technology to
          collect de-identified information relating to your browser or device
          type, the time and date you use the Service, operating system,
          identification of page views, use of particular Service features,
          geographic location and other statistical information relating to your
          use of the Sites or the Service but which does not identify you. This
          information is referred to in this Privacy Policy as
          &ldquo;Analytics.&rdquo; We use Analytics to develop, improve, extend
          and test the Service (and underlying technology platforms) and to
          market and promote Pear Therapeutics and the Service; and we disclose,
          distribute and transmit Analytics to Clinical Partners for their use.
        </p>

        <h3 name="withdrawing_consent">Withdrawing Consent</h3>
        <p>
          You may withdraw your consent to further use of your PII by emailing a
          request to us at{' '}
          <a href={`mailto:${APP_CONFIG.optoutEmail}`}>
            {APP_CONFIG.optoutEmail}
          </a>
          . We will respond to your request in accordance with the law that
          applies to you. Your PII which we processed prior to your request may
          not be deleted from our system records of the Sites but will be
          blocked from further processing without your permission. A request to
          withdraw consent may not apply to information collected by tracking
          technologies or used internally to recognize you and/or facilitate
          your visits to the Sites, or information we may keep to comply with
          legal requirements.
        </p>
        <p>
          You may access, change, modify or delete your information as described
          in the “Accessing Your Information” section below.
        </p>

        <h3 name="protection">Protecting Your Information</h3>
        <p>
          Pear Therapeutics uses secure server software, firewalls, and
          end-to-end encryption to protect your PII from unauthorized access,
          disclosure, alteration, or destruction. However, no method of
          transmission over the Internet, or method of electronic storage, is
          100% secure. Therefore, we cannot guarantee its security.
        </p>
        <p>
          We will retain PII for as long as necessary to provide our services,
          but no later than six (6) years following termination of Services, or
          withdrawal of your consent. We will retain and use PII as necessary to
          comply with our legal obligations, resolve disputes, and enforce our
          agreements.
        </p>

        <h3 name="disclosure">Information Sharing and Disclosure</h3>
        <p>
          Your PII will be shared with your Clinical Partner. We disclose your
          PII as well as de-identified information to third party vendors who
          help us operate the Sites. These third parties are contractually
          obligated to maintain the confidentiality of your PII consistent with
          the terms of this Privacy Policy and to comply with the applicable
          data protection laws.
        </p>
        <p>
          We will disclose your information in response to valid legal process,
          for example, in response to a court order, a subpoena or other legal
          request for information, and/or to comply with applicable legal and
          regulatory reporting requirements. We also may disclose your
          information in response to a law enforcement agency&rsquo;s request or
          other request for information from the U.S. or other government
          entities, or where we believe it is necessary to investigate, prevent,
          or take action regarding illegal activities, suspected fraud,
          situations involving potential threats to the physical safety of any
          person, or to verify or enforce compliance with the policies governing
          our products and/or services and with applicable laws, or as otherwise
          required or permitted by law or consistent with legal requirements. In
          addition, we may, upon notice to you and/or your Clinical Partner,
          transfer your information to an entity or individual that acquires,
          buys, or merges with Pear Therapeutics, or our other business units.
        </p>
        <p>
          We share Analytics with Clinical Partners for their internal use and
          with other third parties to market and promote Pear Therapeutics and
          the Service.
        </p>

        <h3 name="links">
          Links to Other Sites; Third Party Apps; Transactions with Third
          Parties
        </h3>
        <p>
          The Sites may contain links to other sites that are not owned or
          controlled by Pear Therapeutics. Please be aware that we are not
          responsible for the privacy practices of these other sites. We
          encourage you to review the privacy policies and statements of other
          sites to understand their information practices. Our Privacy Policy
          applies only to information collected by our Sites and Services.
        </p>
        <p>
          You may be able to obtain an App, access the Service and/or
          communicate with the Service from, and you may be able to link or
          communicate from the Service to, applications, devices, distribution
          platforms and websites owned and operated by Clinical Partners and/or
          by Apple, Google or other third party distribution platform operators
          (&ldquo;
          <em>
            <strong>Channel Partners</strong>
          </em>
          &rdquo;).&nbsp; These other applications, devices, platforms and
          websites belong to third parties and are not operated or controlled by
          Pear Therapeutics. Our Privacy Policy does not apply to any
          information collected, received, used, processed, transferred or
          disclosed by such third parties. Additional or different terms and
          conditions (including without limitation, privacy and security
          practices) apply when you access and use third party applications,
          devices, platforms and websites, which are not the responsibility of
          Pear Therapeutics.
        </p>
        <p>
          Pear Therapeutics is not responsible for and will not be a party to
          any transactions between you and a third party provider of products,
          information or services. Pear Therapeutics does not monitor such
          transactions or ensure the confidentiality of your PII, including
          credit card information, for any third party transaction. Any separate
          charges or obligations you incur in your dealings with these third
          parties linked to Pear Therapeutics&rsquo;s Sites are solely your
          responsibility.
        </p>

        <h3 name="children">Children</h3>
        <p>
          Our Service is intended for individuals who are over age 18. If you
          believe a child who is under age 13 has used the Service and entered
          personal health information, please contact us using one of the
          options provided below.
        </p>

        <h3 name="rights">
          Your California Privacy Rights; California Do Not Track Disclosures
        </h3>
        <p>
          If you are a California resident, California Civil Code Section
          1798.83 permits you to request information regarding the disclosure of
          your personal informations by Pear Therapeutics or its subsidiaries to
          a third party for the third party&rsquo;s direct marketing purposes.
          Because we do not make such disclosures, we are exempt from these
          reporting requirements.
        </p>
        <p>
          Your browser may offer you a &ldquo;Do Not Track&rdquo; option, which
          allows you to signal to operators of websites and web applications and
          services (including behavioral advertising services) that you do not
          wish such operators to track certain of your online activities, over
          time and across different websites. We do not honor &ldquo;Do Not
          Track&rdquo; signals.
        </p>

        <h3 name="access">Accessing Your Information</h3>
        <p>
          You may access, review, change, update or delete your PII by
          contacting us at{' '}
          <a href={`mailto:${APP_CONFIG.optoutEmail}`}>
            {APP_CONFIG.optoutEmail}
          </a>
          . You can help us maintain the accuracy of your information by
          notifying us of any changes to your information by email to{' '}
          <a href={`mailto:${APP_CONFIG.optoutEmail}`}>
            {APP_CONFIG.optoutEmail}
          </a>
          .
        </p>

        <h3 name="contact_us">
          Contact Us About Complaints, Questions or Notices Relating to this
          Privacy Policy
        </h3>
        <p>
          Pear Therapeutics commits to resolving complaints about your privacy
          and our collection or use of your PII. If you believe your privacy
          rights have been violated or you disagree with any action Pear
          Therapeutics has taken with regard to your PII, you may file a
          complaint with Pear Therapeutics by emailing us at{' '}
          <a href={`mailto:${APP_CONFIG.securityEmail}`}>
            {APP_CONFIG.securityEmail}
          </a>
          .
        </p>
        <p>
          If you have questions or suggestions, please email us at{' '}
          <a href={`mailto:${APP_CONFIG.contactEmail}`}>
            {APP_CONFIG.contactEmail}
          </a>
          . You may also contact us at:
        </p>
        <p translate="no">
          Pear Therapeutics, Inc.
          <br />
          Attn: Security Official
          <br />
          201 Mission Street, Suite 1450
          <br />
          San Francisco, <abbr title="California">CA</abbr> 94105
        </p>

        <h3 name="terms">Terms of Service</h3>
        <p>
          Please also visit our Terms of Service section establishing the use,
          disclaimers, and limitations of liability governing the use of our
          websites at{' '}
          <a href="https://www.pear.md/legal/terms">www.pear.md/legal/terms</a>.
        </p>
        <p>SGA007.F – June 1, 2019</p>
      </div>
    </div>
  );
};

export default Privacy;
